import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Mic, RefreshCw, FileText, Clock, Play, Square, CheckCircle,
  Pause, Save, Edit, Clipboard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceDocumentationDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isRecording, setIsRecording] = useState(false);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('visit_note');
  const [currentSession, setCurrentSession] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase64/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase64/start-session?template=${selectedTemplate}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const session = await response.json();
        setCurrentSession(session);
        setIsRecording(true);
        setShowSessionDialog(false);
        toast.success('Recording session started');
      }
    } catch (error) {
      toast.error('Failed to start session');
    }
  };

  const endSession = async () => {
    if (!currentSession) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase64/end-session/${currentSession.session_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setCurrentSession(null);
        setIsRecording(false);
        toast.success('Session completed and note generated');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to end session');
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="voice-documentation-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Voice-to-Documentation</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">AI-powered clinical note generation from voice</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              {isRecording ? (
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={endSession}>
                  <Square className="h-4 w-4 mr-2" />
                  Stop Recording
                </Button>
              ) : (
                <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowSessionDialog(true)}>
                  <Mic className="h-4 w-4 mr-2" />
                  Start Recording
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="bg-red-600 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
            <span className="font-semibold">Recording in progress...</span>
            <Badge className="bg-white text-red-600 ml-4">Template: {selectedTemplate.replace('_', ' ')}</Badge>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="sessions" className="font-semibold">Sessions</TabsTrigger>
            <TabsTrigger value="templates" className="font-semibold">Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Sessions Today" value={dashboardData?.stats?.sessions_today || 0} icon={Mic} color="teal" />
              <StatCard title="Total Sessions" value={dashboardData?.stats?.total_sessions || 0} icon={FileText} color="blue" />
              <StatCard title="Avg Duration" value={dashboardData?.stats?.avg_session_duration || '0:00'} icon={Clock} color="purple" />
              <StatCard title="Accuracy" value={`${dashboardData?.stats?.transcription_accuracy || 0}%`} icon={CheckCircle} color="green" />
              <StatCard title="Time Saved" value={`${dashboardData?.stats?.time_saved_hours || 0}h`} icon={Clock} color="orange" />
            </div>

            {/* Recent Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Recent Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_sessions?.slice(0, 5).map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-teal-100">
                          <FileText className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{session.patient_name || 'Patient Visit'}</p>
                          <p className="text-sm text-slate-600">{session.template_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={session.status === 'signed' ? 'bg-green-500' : session.status === 'completed' ? 'bg-blue-500' : 'bg-orange-500'}>
                          {session.status}
                        </Badge>
                        <span className="text-sm text-slate-600">{new Date(session.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Voice Documentation Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { tip: 'Speak clearly and at a moderate pace', icon: Mic },
                    { tip: 'State patient name at the beginning', icon: FileText },
                    { tip: 'Use template-specific terminology', icon: Clipboard }
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg flex items-center gap-3">
                      <item.icon className="h-6 w-6 text-teal-600" />
                      <span className="text-slate-700 font-medium">{item.tip}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">All Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Template</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Duration</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Date</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_sessions?.map((session) => (
                        <tr key={session.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{session.patient_name || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{session.template_name}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{Math.floor(session.duration_seconds / 60)}:{(session.duration_seconds % 60).toString().padStart(2, '0')}</td>
                          <td className="px-4 py-3">
                            <Badge className={session.status === 'signed' ? 'bg-green-500' : session.status === 'completed' ? 'bg-blue-500' : 'bg-orange-500'}>
                              {session.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{new Date(session.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="outline">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Available Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData?.templates?.map((template) => (
                    <div key={template} className="p-4 bg-gray-50 rounded-lg border hover:border-teal-300 cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="h-6 w-6 text-teal-600" />
                        <span className="font-semibold text-slate-900 capitalize">{template.replace(/_/g, ' ')}</span>
                      </div>
                      <Button size="sm" className="w-full" variant="outline" onClick={() => {
                        setSelectedTemplate(template);
                        setShowSessionDialog(true);
                      }}>
                        <Play className="h-4 w-4 mr-2" />
                        Use Template
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Start Session Dialog */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">Start Voice Session</DialogTitle>
            <DialogDescription className="text-slate-600">Select a note template and start recording</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-semibold text-slate-900">Note Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {dashboardData?.templates?.map((template) => (
                    <SelectItem key={template} value={template}>{template.replace(/_/g, ' ').toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={startSession}>
              <Mic className="h-4 w-4 mr-2" />
              Start Recording
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceDocumentationDashboard;
